/**
 * Serviço para envio de emails para convites e notificações
 * Implementação usando Firebase para gerenciar os emails
 */

import { getAuth, sendSignInLinkToEmail, sendPasswordResetEmail } from 'firebase/auth';
import { collection, addDoc, serverTimestamp, getFirestore } from 'firebase/firestore';
import { db } from '../firebase/firebase';

// Configurações para o envio de emails
const actionCodeSettings = {
  // URL que o usuário clicará no email para aceitar o convite
  url: window.location.origin + '/accept-invite',
  handleCodeInApp: true
};

/**
 * Envia um email de convite para um profissional ou gestor
 * @param {Object} invitation - Dados do convite
 * @param {Object} salonData - Dados do salão
 * @param {string} senderName - Nome de quem está enviando o convite
 * @returns {Promise<Object>} - Resultado do envio
 */
export const sendInvitationEmail = async (invitation, salonData, senderName) => {
  try {
    const auth = getAuth();
    const db = getFirestore();
    
    // Armazenar dados do convite no Firestore
    const inviteRef = collection(db, 'email_invitations');
    const inviteData = {
      email: invitation.email,
      role: invitation.role,
      salonId: salonData.id || null,
      salonName: salonData.name || 'Novo Salão',
      senderName: senderName || 'Administrador',
      token: invitation.token || null,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: invitation.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    };
    
    // Adicionar convite ao Firestore
    const docRef = await addDoc(inviteRef, inviteData);
    
    // Preparar URL personalizada com o token ou ID do convite
    // Priorizar o uso de token para compatibilidade com InvitationAcceptPage
    const token = invitation.token || docRef.id;
    const inviteUrl = invitation.token 
      ? `${window.location.origin}/accept-invite/${token}?email=${encodeURIComponent(invitation.email)}`
      : `${actionCodeSettings.url}?inviteId=${docRef.id}`;
    
    const customActionCodeSettings = {
      ...actionCodeSettings,
      url: inviteUrl
    };
    
    // Durante o setup de um novo salão, simulamos o envio sem realmente enviar o email
    // para evitar criar contas no Firebase Authentication antes do salão existir
    if (!salonData.id) {
      console.log('[emailService] Simulando envio de convite durante setup:', invitation.email);
      return {
        success: true,
        inviteId: docRef.id,
        token: invitation.token,
        inviteUrl: inviteUrl,
        message: 'Convite simulado durante setup'
      };
    }
    
    // Para convites após o setup, enviar o email real
    // Enviar email de login temporário
    await sendSignInLinkToEmail(auth, invitation.email, customActionCodeSettings);
    
    // Atualizar status do convite no Firestore
    // Esta atualização seria feita em uma Cloud Function no Firebase
    
    return {
      success: true,
      inviteId: docRef.id,
      token: invitation.token,
      inviteUrl: inviteUrl,
      message: 'Convite enviado com sucesso'
    };
  } catch (error) {
    console.error('Erro ao enviar email de convite:', error);
    
    // Mesmo se houver erro no envio de email, não bloqueamos o fluxo
    // Retornamos um objeto simulando que o email foi enviado
    return {
      success: false,
      error: error.message,
      mockEmailSent: true // Flag para indicar que é uma resposta simulada
    };
  }
};

/**
 * Envia um lembrete de convite pendente
 * @param {Object} invitation - Dados do convite
 * @param {Object} salonData - Dados do salão
 * @returns {Promise<Object>} - Resultado do envio
 */
export const sendInvitationReminder = async (invitation, salonData) => {
  try {
    const auth = getAuth();
    
    // Se o salão ainda não existir (durante setup), apenas simular o envio
    if (!salonData.id) {
      console.log('[emailService] Simulando envio de lembrete durante setup:', invitation.email);
      return {
        success: true,
        message: 'Lembrete simulado durante setup'
      };
    }
    
    // Para lembretes após o setup, enviar usando o Firebase
    // Priorizar o uso de token para compatibilidade com InvitationAcceptPage
    const inviteUrl = invitation.token 
      ? `${window.location.origin}/accept-invite/${invitation.token}?email=${encodeURIComponent(invitation.email)}&reminder=true`
      : `${actionCodeSettings.url}?inviteId=${invitation.id}&reminder=true`;
    
    const customActionCodeSettings = {
      ...actionCodeSettings,
      url: inviteUrl
    };
    
    await sendSignInLinkToEmail(auth, invitation.email, customActionCodeSettings);
    
    return {
      success: true,
      message: 'Lembrete enviado com sucesso'
    };
  } catch (error) {
    console.error('Erro ao enviar lembrete de convite:', error);
    
    // Retornamos um objeto simulando que o email foi enviado
    return {
      success: false,
      error: error.message,
      mockEmailSent: true
    };
  }
};

/**
 * Função para verificar se a API de emails está disponível
 * @returns {Promise<boolean>} - true se a API estiver disponível
 */
export const checkEmailServiceAvailability = async () => {
  try {
    const auth = getAuth();
    // Consideramos o serviço disponível se o Firebase Auth estiver inicializado
    return !!auth;
  } catch (error) {
    console.error('Serviço de email não disponível:', error);
    return false;
  }
}; 